import { DateRangeFilterDrawer, type DateRange } from '@client/features/advisor-dashboard';
import { cn } from '@client/lib';
import { ModalState } from '@client/pages';
import { useMemo, useState } from 'react';
import { ClientActionSheet } from './ClientActionSheet';
import { ClientCard } from './ClientCard';
import { SearchHeader } from './SearchHeader';
import { SampleClientsToggle } from '@/shared/components/sample-clients-toggle';

type DashboardCardViewProps = {
  clients: Client[];
  sessionAdvisor: Advisor;
  subordinateAdvisors: Advisor[];
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
  isLoading: boolean;
  handleRefresh: () => void;
  globalSearchText: string;
  handleSearchTextChange: (searchText: string) => void;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
  hasSampleClients?: boolean;
  showSampleClients?: boolean;
  onToggleSampleClients?: (show: boolean) => void;
  hiddenColumnIds?: string[];
};

type ClientCardListProps = {
  isLoading: boolean;
  filteredClients: Client[];
  clients: Client[];
  sessionAdvisor: Advisor;
  subordinateAdvisors: Advisor[];
  setSelectedClient: (client: Client) => void;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
  hiddenColumnIds: string[];
};

function ClientCardSkeleton() {
  return (
    <div className="animate-pulse rounded-xl bg-white p-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 rounded bg-gray-300" />
          <div className="h-4 w-20 rounded bg-gray-200" />
        </div>
      </div>

      <div className="mt-4 h-10 w-full rounded-lg bg-gray-200" />
    </div>
  );
}

function ClientCardList({
  isLoading,
  filteredClients,
  clients,
  sessionAdvisor,
  subordinateAdvisors,
  setSelectedClient,
  setModalState,
  hiddenColumnIds,
}: ClientCardListProps) {
  return (
    <div className="space-y-4">
      {isLoading ? (
        Array.from({ length: 5 }).map((_, idx) => <ClientCardSkeleton key={idx} />)
      ) : filteredClients.length > 0 ? (
        filteredClients.map(client => (
          <ClientCard
            key={client.clientId}
            client={client}
            clients={clients}
            sessionAdvisor={sessionAdvisor}
            subordinateAdvisors={subordinateAdvisors}
            onMenuClick={() => setSelectedClient(client)}
            setModalState={setModalState}
            hiddenColumnIds={hiddenColumnIds}
          />
        ))
      ) : (
        <p className="px-4 text-sm text-gray-700">No results found</p>
      )}
    </div>
  );
}

export function DashboardCardView({
  clients,
  sessionAdvisor,
  subordinateAdvisors,
  setModalState,
  isLoading,
  handleRefresh,
  globalSearchText,
  handleSearchTextChange,
  dateRange,
  onDateRangeChange,
  hasSampleClients,
  showSampleClients,
  onToggleSampleClients,
  hiddenColumnIds = [],
}: DashboardCardViewProps) {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const filteredClients = useMemo(() => {
    let list = [...clients];

    if (globalSearchText?.trim()) {
      const term = globalSearchText.toLowerCase();

      list = list.filter(client => {
        const fullName = `${client.clientFirstName} ${client.clientLastName}`.toLowerCase();
        const email = client.clientEmail?.toLowerCase() ?? '';
        return fullName.includes(term) || email.includes(term);
      });
    }

    list.sort((a, b) => {
      const aDate = new Date(a.clientAddedDateTime).getTime();
      const bDate = new Date(b.clientAddedDateTime).getTime();
      return bDate - aDate;
    });

    return list;
  }, [clients, globalSearchText]);

  const memoizedMobileCardList = useMemo(() => {
    return (
      <ClientCardList
        clients={clients}
        isLoading={isLoading}
        filteredClients={filteredClients}
        sessionAdvisor={sessionAdvisor}
        subordinateAdvisors={subordinateAdvisors}
        setSelectedClient={setSelectedClient}
        setModalState={setModalState}
        hiddenColumnIds={hiddenColumnIds}
      />
    );
  }, [clients, isLoading, globalSearchText, subordinateAdvisors, hiddenColumnIds]);

  const text =
    filteredClients.length === clients.length
      ? `Showing ${clients.length} clients`
      : `Showing ${filteredClients.length} of ${clients.length} clients`;

  return (
    <>
      <div
        className={cn(
          'bg-[#f5f3ff] transition-all duration-200',
          selectedClient && 'pointer-events-none opacity-40 blur-[1px]',
        )}
      >
        <SearchHeader
          searchTerm={globalSearchText || ''}
          onSearchChange={handleSearchTextChange}
          handleRefresh={handleRefresh}
          extraButtons={
            <>
              {hasSampleClients && onToggleSampleClients && showSampleClients !== undefined && (
                <SampleClientsToggle
                  showSampleClients={showSampleClients}
                  onToggle={onToggleSampleClients}
                  isMobile
                />
              )}
              {onDateRangeChange && (
                <DateRangeFilterDrawer
                  dateRange={dateRange}
                  onDateRangeChange={onDateRangeChange}
                />
              )}
            </>
          }
        />
        <p className="text-muted-foreground text-primary mt-2 px-4 text-xs">{text}</p>

        <div className="overflow-y-auto px-4 pb-4 pt-2">{memoizedMobileCardList}</div>
      </div>

      {selectedClient && (
        <ClientActionSheet
          client={selectedClient}
          sessionAdvisor={sessionAdvisor}
          onClose={() => setSelectedClient(null)}
          setModalState={setModalState}
        />
      )}
    </>
  );
}
